import React from 'react';
import PropTypes from 'prop-types';
import { CardCvcElement, CardExpiryElement, CardNumberElement } from '@stripe/react-stripe-js';
import styled from 'styled-components';

import { Popup } from '@shared/Popup';
import Btn from '../../../buttons/Btn';
import Cross from '../../../../images/icon-close.inline.svg';
import Logo from '../../../../images/logo-car.inline.svg';
import {
  MAIN_BLUE, ERROR_COLOR, NOTES_COLOR, TEXT_COLOR,
} from '../../../../theme';

const CardNumberRow = styled.div`
  border: 1px solid hsla(240,1%,49%,.25);
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  padding: 9px;
  margin-top: 18px;
`;

const DateCvcRow = styled.div`
  display: flex;
  border: 1px solid hsla(240,1%,49%,.25);
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  border-top: none;
  margin-bottom: 8px;
`;

const DateCell = styled.div`
  flex: 1 0 50%;
  border-right: 1px solid hsla(240,1%,49%,.25);
  padding: 9px;
`;

const CvcCell = styled.div`
  flex: 1 0 50%;
  padding: 9px;
`;

const CrossIcon = styled(Cross)`
  position: absolute;
  top: 8px;
  right: 8px;
  cursor: pointer;
  width: 16px;
  height: 16px;
  
  path {
    fill: #999;
  }
`;

const Header = styled.div`
  margin: -32px -40px 0 -40px;
  background: #e8e9eb;
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 47px 36px 12px;
  position: relative;
  border-bottom: 1px solid #d2d2d3;
  text-align: center;
`;

const LogoContainer = styled.div`
  width: 70px;
  height: 70px;
  border: 3px solid #fff;
  border-radius: 100%;
  box-shadow: 0 0 0 1px rgba(0,0,0,.18), 0 2px 2px 0 rgba(0,0,0,.08);
  position: absolute;
  top: -38px;
  left: 50%;
  margin-left: -35px;
  overflow: hidden;
`;

const LogoImg = styled(Logo)`
  width: 76px;
  height: 76px;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -35px);
`;

const ProductTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  line-height: 1.5;
  margin: 0;
`;

const Description = styled.div`
  color: ${NOTES_COLOR};
  font-size: 13px;
  line-height: 1.5;
  margin-top: 6px;
`;

const CardError = styled.div`
  color: ${ERROR_COLOR};
  margin-bottom: 16px;
`;

const INPUT_OPTIONS = {
  style: {
    base: {
      fontSize: '15px',
      color: `${TEXT_COLOR}`,
      iconColor: `${MAIN_BLUE}`,
    },
    invalid: {
      color: `${ERROR_COLOR}`,
      '::placeholder': {
        color: '#ff93a3',
      },
    },
  },
};

const CheckoutFormComponent = ({
  stripe, isSending, onClose, schoolName, handleSubmit, cost, error, setCardNumberReady, setCardExpiryReady, setCardCvcReady, isFormReady,
}) => (
  <Popup width={300} overflow="visible">
    <Header>
      <CrossIcon onClick={onClose} />
      <LogoContainer>
        <LogoImg />
      </LogoContainer>
      <ProductTitle>Accès code en ligne</ProductTitle>
      <Description>{`avec ${schoolName}`}</Description>
    </Header>
    <form onSubmit={handleSubmit}>
      <CardNumberRow>
        <CardNumberElement options={INPUT_OPTIONS} onReady={() => setCardNumberReady(true)} />
      </CardNumberRow>
      <DateCvcRow>
        <DateCell>
          <CardExpiryElement options={INPUT_OPTIONS} onReady={() => setCardExpiryReady(true)} />
        </DateCell>
        <CvcCell>
          <CardCvcElement options={INPUT_OPTIONS} onReady={() => setCardCvcReady(true)} />
        </CvcCell>
      </DateCvcRow>
      <CardError>{error}</CardError>
      <Btn type="submit" disabled={!stripe || !isFormReady || isSending} isLoading={isSending} text={`Payer ${cost}€`} block />
    </form>
  </Popup>
);

CheckoutFormComponent.propTypes = {
  stripe: PropTypes.object,
  isSending: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  schoolName: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  cost: PropTypes.string.isRequired,
  error: PropTypes.string,
  setCardNumberReady: PropTypes.func.isRequired,
  setCardExpiryReady: PropTypes.func.isRequired,
  setCardCvcReady: PropTypes.func.isRequired,
  isFormReady: PropTypes.bool.isRequired,
};

CheckoutFormComponent.defaultProps = {
  stripe: null,
  error: '',
};

export { CheckoutFormComponent };
